import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { getOverride, getEstimatedVotesPercent, getRaceEevpSentence, getAffiliationColor } from "../helpers";
import "./live-ticker.scss";
import ErrorBoundary from "../ErrorBoundary";

/**
 * A single race slide in the ticker showing the top 2 candidates side by side.
 */
const TickerSlide = ({ race }) => {
  const candidates = race.candidates || [];
  const sorted = [...candidates].sort((a, b) => {
    const votesA = getOverride(a, "votes") || 0;
    const votesB = getOverride(b, "votes") || 0;
    return votesB - votesA;
  });
  const top2 = sorted.slice(0, 2);
  const raceName = getOverride(race, "name") || "Race";
  // AP estimated expected vote percentage (eevp), whole number or null when blank. Never calculated; precinctsPercent is not used.
  const precinctsPercent = getEstimatedVotesPercent(race);
  // Same sentence/rounding rule as the maps (round half up, per apelectionresults.py)
  const eevpSentence = getRaceEevpSentence(race);

  return (
    <div className="ticker-slide">
      <div className="ticker-race-name" title={raceName}>{raceName}</div>
      <div className="ticker-candidates">
        {top2.map((cand, i) => {
          const name = getOverride(cand, "name") || "";
          const party = getOverride(cand, "party") || "";
          const percentage = getOverride(cand, "percentage") || 0;
          const winner = getOverride(cand, "winner") || false;
          const partyColor = getAffiliationColor(party);

          return (
            <div key={i} className={`ticker-candidate ${winner ? "winner" : ""}`}>
              <span
                className="ticker-party-dot"
                style={{ backgroundColor: partyColor }}
              ></span>
              <span className="ticker-cand-name" title={name}>{name}</span>
              <span className="ticker-cand-pct">{percentage}%</span>
              {winner && <span className="ticker-checkmark">&#10003;</span>}
            </div>
          );
        })}
      </div>
      {precinctsPercent !== null && (
        <div className="ticker-precincts">
          <div className="ticker-precincts-bar">
            <div
              className="ticker-precincts-fill"
              style={{ width: `${precinctsPercent}%` }}
            ></div>
          </div>
          <span className="ticker-precincts-label">{eevpSentence || `${precinctsPercent}% of the estimated votes counted`}</span>
        </div>
      )}
    </div>
  );
};

// Cards per row, based on the widget's own width in px. About 136px goes to the padding and
// the two arrows, so cards stay around 235px or wider from these breakpoints up.
const getVisibleCount = (width) => {
  if (width < 640) return 1;
  if (width < 880) return 2;
  return 3;
};

/**
 * Live Ticker Widget - A horizontal scrolling/auto-rotating banner
 * showing key race results in a compact format.
 */
const LiveTickerWidget = (props) => {
  const races = props.races || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-rotate every 4 seconds
  const rotateInterval = 4000;

  // How many slides to show at once. Chosen from the widget's own width (not the window),
  // so a narrow column on a wide screen still gets full-size cards.
  const [visibleCount, setVisibleCount] = useState(1);
  const [trackWidth, setTrackWidth] = useState(0);
  const rootRef = useRef(null);
  const wrapperRef = useRef(null);
  const trackRef = useRef(null);
  const hasRaces = races.length > 0;
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const measure = () => {
      setVisibleCount(getVisibleCount(root.clientWidth));
      if (wrapperRef.current) setTrackWidth(wrapperRef.current.clientWidth);
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(root);
      if (wrapperRef.current) observer.observe(wrapperRef.current);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [hasRaces]);
  const totalSlides = races.length;
  const slotWidth = `${100 / visibleCount}%`;
  const slotStyle = { flex: `0 0 ${slotWidth}`, width: slotWidth, maxWidth: slotWidth };
  const maxIndex = Math.max(0, totalSlides - visibleCount);
  // Never render past the last valid position (visibleCount or race count can shrink).
  const activeIndex = Math.min(currentIndex, maxIndex);
  useEffect(() => {
    if (currentIndex > maxIndex) setCurrentIndex(maxIndex);
  }, [currentIndex, maxIndex]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (Math.min(prev, maxIndex) >= maxIndex ? 0 : Math.min(prev, maxIndex) + 1));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : Math.min(prev, maxIndex) - 1));
  }, [maxIndex]);

  useEffect(() => {
    if (isPaused || totalSlides <= visibleCount) return;
    const timer = setInterval(goNext, rotateInterval);
    return () => clearInterval(timer);
  }, [isPaused, goNext, totalSlides, visibleCount]);

  // Move the track by the measured distance to the active slot. Every slot is exactly
  // 1/visibleCount of the track, and measuring the real position means sub-pixel rounding
  // can never build up from one step to the next.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const slot = track && track.children[activeIndex];
    if (!slot) return;
    const offset = slot.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.style.transform = `translateX(${-offset}px)`;
  }, [activeIndex, visibleCount, trackWidth, totalSlides]);

  if (!races.length) {
    return <div className="live-ticker empty">No race data available</div>;
  }

  return (
    <div
      className="live-ticker"
      ref={rootRef}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") setIsPaused(true); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") setIsPaused(false); }}
      role="region"
      aria-label="Election results live ticker"
    >
      {props.widget.showTitle && (
        <div className="ticker-header">
          <span className="ticker-live-dot"></span>
          <span className="ticker-title">
            {props.widget.title || "Live Results"}
          </span>
        </div>
      )}

      <div className="ticker-viewport">
        {totalSlides > visibleCount && (
          <button
            className="ticker-nav ticker-nav-prev"
            onClick={goPrev}
            aria-label="Previous races"
          >
            &#8249;
          </button>
        )}

        <div className="ticker-track-wrapper" ref={wrapperRef}>
          <div
            className="ticker-track"
            ref={trackRef}
            style={{ transition: "transform 0.4s ease-in-out" }}
          >
            {races.map((race, index) => (
              <div
                key={index}
                className="ticker-slide-wrapper"
                style={slotStyle}
              >
                <ErrorBoundary message={process.env.REACT_APP_ERROR_MESSAGE}>
                  <TickerSlide race={race} />
                </ErrorBoundary>
              </div>
            ))}
          </div>
        </div>

        {totalSlides > visibleCount && (
          <button
            className="ticker-nav ticker-nav-next"
            onClick={goNext}
            aria-label="Next races"
          >
            &#8250;
          </button>
        )}
      </div>

      {totalSlides > visibleCount && (
        <div className="ticker-dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              className={`ticker-dot ${i === activeIndex ? "active" : ""}`}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveTickerWidget;
